import React, { useEffect, useState } from 'react';
import { db, functions } from '../../firebase/config';
// ✅ ADDED: getDoc to the import list
import { collection, getCountFromServer, query, where, getDocs, doc, deleteDoc, updateDoc, serverTimestamp, addDoc, setDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  // ✅ ADDED: totalViews to the initial stats state
  const [stats, setStats] = useState({ userCount: 0, activeListings: 0, totalRevenue: 0, totalViews: 0 });
  const [allListings, setAllListings] = useState([]); 
  const [usersList, setUsersList] = useState([]);
  const [soldLedger, setSoldLedger] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Featured State
  const [togglingFeature, setTogglingFeature] = useState(null);

  // Support Reply States
  const [replyingTo, setReplyingTo] = useState(null); 
  const [replyMessage, setReplyMessage] = useState('');

  // Broadcast States
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastType, setBroadcastType] = useState('info'); 

  // Category States
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        const userSnapshot = await getCountFromServer(collection(db, "users"));
        const activeSnapshot = await getCountFromServer(query(collection(db, "listings"), where("status", "==", "active")));
        
        // ✅ NEW: Fetch the view counter from the analytics collection
        const trafficRef = doc(db, "analytics", "traffic");
        const trafficSnap = await getDoc(trafficRef);
        const views = trafficSnap.exists() ? trafficSnap.data().viewCount : 0;

        const soldQuery = query(collection(db, "listings"), where("status", "==", "sold"));
        const soldSnap = await getDocs(soldQuery);
        const soldData = soldSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSoldLedger(soldData);

        let revenue = 0;
        soldData.forEach(item => {
          if (item.sellerRole !== 'admin') {
            const itemTotal = (Number(item.price) || 0) + (Number(item.shippingCost) || 0);
            revenue += itemTotal * 0.07;
          }
        });

        // ✅ ADDED: Included totalViews in the state update
        setStats({ 
          userCount: userSnapshot.data().count, 
          activeListings: activeSnapshot.data().count, 
          totalRevenue: revenue,
          totalViews: views 
        });

        const listingsQuery = query(collection(db, "listings"));
        const listSnap = await getDocs(listingsQuery);
        const sortedListings = listSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
        setAllListings(sortedListings);

        const usersQuery = query(collection(db, "users"));
        const usersSnap = await getDocs(usersQuery);
        const sortedUsers = usersSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.joinedAt?.toMillis() || 0) - (a.joinedAt?.toMillis() || 0));
        setUsersList(sortedUsers);

        const ticketsQuery = query(collection(db, "support_tickets"), where("status", "==", "open"));
        const ticketsSnap = await getDocs(ticketsQuery);
        const fetchedTickets = ticketsSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
        setSupportTickets(fetchedTickets);

        const catSnap = await getDocs(collection(db, "categories"));
        setCategories(catSnap.docs.map(doc => doc.id).sort());

      } catch (error) {
        console.error("Admin Fetch Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, []);

  const handleDeleteListing = async (id) => {
    if (window.confirm("🚨 Force delete this listing? This cannot be undone.")) {
      try {
        await deleteDoc(doc(db, "listings", id));
        setAllListings(prev => prev.filter(item => item.id !== id));
        alert("Listing permanently deleted.");
      } catch (error) { alert("Error deleting listing."); }
    }
  };

  const handleRestoreListing = async (id) => {
    if (window.confirm("Restore this item? It will clear all flags and return to the active feed.")) {
      try {
        await updateDoc(doc(db, "listings", id), { status: 'active', flags: [], flagCount: 0 });
        setAllListings(prev => prev.map(item => item.id === id ? { ...item, status: 'active', flags: [], flagCount: 0 } : item));
        alert("Listing restored to active feed.");
      } catch (error) {
        console.error("Error restoring:", error);
        alert("Error restoring listing.");
      }
    }
  };

  const handleToggleFeature = async (itemId, currentStatus) => {
    setTogglingFeature(itemId);
    try {
      await updateDoc(doc(db, "listings", itemId), { isFeatured: !currentStatus });
      setAllListings(prev => prev.map(item => item.id === itemId ? { ...item, isFeatured: !currentStatus } : item));
    } catch (error) {
      console.error("Error updating feature status:", error);
      alert("Failed to feature item.");
    } finally {
      setTogglingFeature(null);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (window.confirm(`Change this user's role to ${newRole.toUpperCase()}?`)) {
      try {
        const updateRoleFn = httpsCallable(functions, 'updateUserRole');
        await updateRoleFn({ targetUserId: userId, newRole: newRole });
        
        setUsersList(prev => prev.map(user => user.id === userId ? { ...user, role: newRole } : user));
        alert(`Success! User is now an ${newRole.toUpperCase()}.`);
      } catch (error) { 
        console.error(error);
        alert("Error updating user role: " + error.message); 
      }
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (window.confirm(`🚨 PERMANENT DELETE: Are you sure you want to delete ${userEmail}? This will permanently remove their profile data.`)) {
      try {
        await deleteDoc(doc(db, "users", userId));
        setUsersList(prev => prev.filter(user => user.id !== userId));
        alert("User profile has been successfully deleted.");
      } catch (error) { 
        console.error(error);
        alert("Error deleting user profile."); 
      }
    }
  };

  const handleResolveTicket = async (ticketId) => {
    try {
      await updateDoc(doc(db, "support_tickets", ticketId), { status: 'resolved' });
      setSupportTickets(prev => prev.filter(t => t.id !== ticketId));
    } catch (error) { alert("Failed to resolve ticket."); }
  };

  const handleSendReply = async (e, ticket) => {
    e.preventDefault();
    if (!replyMessage.trim()) return alert("Please type a reply first.");

    try {
      await addDoc(collection(db, `support_tickets/${ticket.id}/replies`), {
        message: replyMessage,
        sentAt: serverTimestamp(),
        adminEmail: "nundinaria.admin@gmail.com",
        userEmail: ticket.userEmail,
        originalSubject: ticket.subject
      });

      await handleResolveTicket(ticket.id);

      setReplyingTo(null);
      setReplyMessage('');
      alert("Reply sent! The ticket has been resolved.");
      
    } catch (error) {
      console.error("Error sending reply:", error);
      alert("Failed to send reply.");
    }
  };

  const handleSendBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcastMessage.trim()) return alert("Please enter a message to broadcast.");
    if (!window.confirm("Are you sure you want to push this alert to the entire platform?")) return;

    try {
      await setDoc(doc(db, "system", "global_alert"), {
        message: broadcastMessage,
        type: broadcastType,
        active: true,
        updatedAt: serverTimestamp()
      });
      
      setBroadcastMessage('');
      alert("Broadcast sent successfully!");
    } catch (error) {
      console.error("Broadcast failed:", error);
      alert("Failed to send broadcast.");
    }
  };

  const handleClearBroadcast = async () => {
    try {
      await updateDoc(doc(db, "system", "global_alert"), { active: false });
      alert("Global alert cleared.");
    } catch (error) {
      console.error("Clear failed:", error);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;
    
    const formattedCat = newCategory.trim().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');

    if (categories.includes(formattedCat)) return alert("Category already exists!");

    try {
      await setDoc(doc(db, "categories", formattedCat), { active: true });
      setCategories(prev => [...prev, formattedCat].sort());
      setNewCategory('');
    } catch (error) {
      console.error("Add category failed:", error);
      alert("Failed to add category.");
    }
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`Delete the category "${cat}"? Active listings will still keep their category tag, but it will be removed from the dropdown menus.`)) return;
    
    try {
      await deleteDoc(doc(db, "categories", cat));
      setCategories(prev => prev.filter(c => c !== cat));
    } catch (error) {
      console.error("Delete category failed:", error);
    }
  };

  const quarantinedListings = allListings.filter(i => i.status === 'quarantined');

  if (loading) return <div style={{ padding: '100px', textAlign: 'center', fontSize: '20px', fontWeight: 'bold' }}>Authenticating Admin Credentials...</div>;

  return (
    <div style={{ maxWidth: '1200px', margin: '40px auto', padding: '20px' }}>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1 style={{ margin: '0 0 5px 0', fontSize: '32px' }}>Admin Control Center</h1>
          <p style={{ margin: 0, color: '#6c757d', fontWeight: 'bold' }}>God Mode Authorized</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '30px', borderBottom: '2px solid #e9ecef', paddingBottom: '10px', overflowX: 'auto' }}>
        <button onClick={() => setActiveTab('overview')} style={tabStyle(activeTab === 'overview')}>📊 Overview</button>
        <button onClick={() => setActiveTab('moderation')} style={tabStyle(activeTab === 'moderation')}>🛡️ Moderation ({allListings.length})</button>
        <button onClick={() => setActiveTab('quarantine')} style={tabStyle(activeTab === 'quarantine')}>🚩 Quarantine {quarantinedListings.length > 0 && <span style={{ backgroundColor: '#dc3545', color: 'white', padding: '2px 8px', borderRadius: '50px', fontSize: '11px', marginLeft: '5px' }}>{quarantinedListings.length}</span>}</button>
        <button onClick={() => setActiveTab('users')} style={tabStyle(activeTab === 'users')}>👥 Users ({usersList.length})</button>
        <button onClick={() => setActiveTab('ledger')} style={tabStyle(activeTab === 'ledger')}>🧾 Ledger ({soldLedger.length})</button>
        <button onClick={() => setActiveTab('support')} style={tabStyle(activeTab === 'support')}>🎫 Support {supportTickets.length > 0 && <span style={{ backgroundColor: '#dc3545', color: 'white', padding: '2px 8px', borderRadius: '50px', fontSize: '11px', marginLeft: '5px' }}>{supportTickets.length}</span>}</button>
        <button onClick={() => setActiveTab('broadcast')} style={tabStyle(activeTab === 'broadcast')}>📢 Broadcast</button>
        <button onClick={() => setActiveTab('categories')} style={tabStyle(activeTab === 'categories')}>🗂️ Categories</button>
      </div>

      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '25px' }}>
          
          {/* ✅ NEW: Total Site Views Card */}
          <div style={{...statCardStyle, borderLeftColor: '#6f42c1'}}>
            <h3 style={{ margin: '0 0 10px 0', color: '#6c757d', fontSize: '14px', textTransform: 'uppercase' }}>Total Site Views</h3>
            <p style={{ margin: 0, fontSize: '36px', fontWeight: '900', color: '#6f42c1' }}>{stats.totalViews.toLocaleString()}</p>
          </div>

          <div style={statCardStyle}>
            <h3 style={{ margin: '0 0 10px 0', color: '#6c757d', fontSize: '14px', textTransform: 'uppercase' }}>Total Revenue (7% Cut)</h3>
            <p style={{ margin: 0, fontSize: '36px', fontWeight: '900', color: '#28a745' }}>${stats.totalRevenue.toFixed(2)}</p>
          </div>
          <div style={{...statCardStyle, borderLeftColor: '#007bff'}}>
            <h3 style={{ margin: '0 0 10px 0', color: '#6c757d', fontSize: '14px', textTransform: 'uppercase' }}>Registered Users</h3>
            <p style={{ margin: 0, fontSize: '36px', fontWeight: '900', color: '#007bff' }}>{stats.userCount}</p>
          </div>
          <div style={{...statCardStyle, borderLeftColor: '#17a2b8'}}>
            <h3 style={{ margin: '0 0 10px 0', color: '#6c757d', fontSize: '14px', textTransform: 'uppercase' }}>Active Listings</h3>
            <p style={{ margin: 0, fontSize: '36px', fontWeight: '900', color: '#17a2b8' }}>{stats.activeListings}</p>
          </div>
        </div>
      )}

      {activeTab === 'quarantine' && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', overflow: 'hidden', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '600px' }}>
            <thead style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <tr><th style={{ padding: '15px' }}>Reported Item</th><th style={{ padding: '15px' }}>Flags</th><th style={{ padding: '15px' }}>Seller</th><th style={{ padding: '15px' }}>Action</th></tr>
            </thead>
            <tbody>
              {quarantinedListings.length === 0 && (
                <tr><td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#888' }}>No quarantined items. Safe and sound!</td></tr>
              )}
              {quarantinedListings.map(item => (
                <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img src={item.mainImage || 'https://via.placeholder.com/40'} alt="Item" style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover' }} />
                    <a href={`/item/${item.id}`} target="_blank" rel="noreferrer" style={{ color: '#007bff', textDecoration: 'none', fontWeight: 'bold' }}>{item.title}</a>
                  </td>
                  <td style={{ padding: '15px' }}>
                    <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', backgroundColor: '#f8d7da', color: '#721c24' }}>
                      {item.flagCount || 3} Reports
                    </span>
                  </td>
                  <td style={{ padding: '15px', color: '#495057' }}>{item.sellerEmail}</td>
                  <td style={{ padding: '15px', display: 'flex', gap: '8px' }}>
                    <button onClick={() => handleRestoreListing(item.id)} style={btnStyle('#28a745')}>Restore</button>
                    <button onClick={() => handleDeleteListing(item.id)} style={btnStyle('#dc3545')}>Force Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'moderation' && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', overflow: 'hidden', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '600px' }}>
            <thead style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <tr><th style={{ padding: '15px' }}>Item</th><th style={{ padding: '15px' }}>Status</th><th style={{ padding: '15px' }}>Seller</th><th style={{ padding: '15px' }}>Action</th></tr>
            </thead>
            <tbody>
              {allListings.length === 0 && (
                <tr><td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#888' }}>No listings found.</td></tr>
              )}
              {allListings.map(item => (
                <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img src={item.mainImage || 'https://via.placeholder.com/40'} alt="Item" style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover' }} />
                    <div>
                      <a href={`/item/${item.id}`} target="_blank" rel="noreferrer" style={{ color: '#007bff', textDecoration: 'none', fontWeight: 'bold', display: 'block' }}>{item.title}</a>
                      {item.isFeatured && <span style={{ fontSize: '11px', color: '#ffc107', fontWeight: 'bold' }}>⭐ FEATURED</span>}
                    </div>
                  </td>
                  <td style={{ padding: '15px' }}>
                    <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', backgroundColor: item.status === 'active' ? '#cce5ff' : item.status === 'quarantined' ? '#f8d7da' : '#e2e3e5', color: item.status === 'active' ? '#004085' : item.status === 'quarantined' ? '#721c24' : '#383d41' }}>
                      {item.status?.toUpperCase() || 'UNKNOWN'}
                    </span>
                  </td>
                  <td style={{ padding: '15px', color: '#495057' }}>{item.sellerEmail}</td>
                  <td style={{ padding: '15px', display: 'flex', gap: '8px' }}>
                    <button 
                      onClick={() => handleToggleFeature(item.id, item.isFeatured)} 
                      disabled={togglingFeature === item.id}
                      style={{ padding: '6px 12px', backgroundColor: item.isFeatured ? '#fff3cd' : '#f8f9fa', color: item.isFeatured ? '#856404' : '#495057', border: `1px solid ${item.isFeatured ? '#ffeeba' : '#dee2e6'}`, borderRadius: '4px', cursor: togglingFeature === item.id ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 'bold', minWidth: '110px' }}
                    >
                      {togglingFeature === item.id ? 'Saving...' : (item.isFeatured ? '⭐ Un-Feature' : '☆ Feature Item')}
                    </button>
                    <button onClick={() => handleDeleteListing(item.id)} style={{ padding: '6px 12px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>Force Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'users' && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', overflow: 'hidden', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '600px' }}>
            <thead style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <tr><th style={{ padding: '15px' }}>Email</th><th style={{ padding: '15px' }}>Joined</th><th style={{ padding: '15px' }}>Role</th><th style={{ padding: '15px' }}>Controls</th></tr>
            </thead>
            <tbody>
              {usersList.map(user => (
                <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '15px', fontWeight: 'bold', color: '#212529' }}>{user.email}</td>
                  <td style={{ padding: '15px', color: '#6c757d', fontSize: '14px' }}>{user.joinedAt?.toDate().toLocaleDateString() || 'N/A'}</td>
                  <td style={{ padding: '15px' }}><span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', backgroundColor: user.role === 'admin' ? '#cce5ff' : user.role === 'suspended' ? '#f8d7da' : '#e2e3e5', color: user.role === 'admin' ? '#004085' : user.role === 'suspended' ? '#721c24' : '#383d41' }}>{user.role?.toUpperCase() || 'USER'}</span></td>
                  <td style={{ padding: '15px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {user.role !== 'admin' ? (
                      <>
                        <button onClick={() => handleRoleChange(user.id, 'admin')} style={btnStyle('#28a745')}>Make Admin</button>
                        <button onClick={() => handleRoleChange(user.id, 'suspended')} style={btnStyle('#fd7e14')}>Ban User</button>
                        <button onClick={() => handleDeleteUser(user.id, user.email)} style={btnStyle('#dc3545')}>Delete User</button>
                      </>
                    ) : (
                      <button onClick={() => handleRoleChange(user.id, 'user')} style={btnStyle('#ffc107', '#212529')}>Revoke Admin</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'ledger' && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', overflow: 'hidden', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '600px' }}>
            <thead style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <tr><th style={{ padding: '15px' }}>Sold Item</th><th style={{ padding: '15px' }}>Total Price</th><th style={{ padding: '15px' }}>Platform Fee (7%)</th><th style={{ padding: '15px' }}>Tracking #</th></tr>
            </thead>
            <tbody>
              {soldLedger.length === 0 && <tr><td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#888' }}>No completed sales yet.</td></tr>}
              {soldLedger.map(item => {
                const total = (Number(item.price) || 0) + (Number(item.shippingCost) || 0);
                const fee = item.sellerRole === 'admin' ? 0 : total * 0.07;
                return (
                  <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '15px' }}><a href={`/item/${item.id}`} target="_blank" rel="noreferrer" style={{ color: '#007bff', textDecoration: 'none', fontWeight: 'bold' }}>{item.title}</a><br/><span style={{ fontSize: '12px', color: '#6c757d' }}>Seller: {item.sellerEmail}</span></td>
                    <td style={{ padding: '15px', fontWeight: 'bold' }}>${total.toFixed(2)}</td>
                    <td style={{ padding: '15px', color: fee > 0 ? '#28a745' : '#6c757d', fontWeight: 'bold' }}>{fee > 0 ? `+$${fee.toFixed(2)}` : 'Waived'}</td>
                    <td style={{ padding: '15px' }}>{item.trackingNumber ? <span style={{ color: '#004085', backgroundColor: '#cce5ff', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>{item.trackingNumber}</span> : <span style={{ color: '#856404' }}>Pending</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'support' && (
        <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <h2 style={{ marginBottom: '20px' }}>Active Support Tickets</h2>
          {supportTickets.length === 0 ? (
            <p style={{ color: '#888', fontStyle: 'italic' }}>Inbox zero! No open support tickets right now.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {supportTickets.map(ticket => (
                <div key={ticket.id} style={{ border: '1px solid #e9ecef', borderRadius: '8px', padding: '20px', backgroundColor: '#f8f9fa' }}>
                  
                  {/* Ticket Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                    <div>
                      <h3 style={{ margin: '0 0 5px 0', fontSize: '18px', color: '#1a1a1a' }}>{ticket.subject}</h3>
                      <p style={{ margin: 0, fontSize: '13px', color: '#6c757d' }}>From: <strong>{ticket.userEmail}</strong> | {ticket.createdAt?.toDate().toLocaleString()}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => setReplyingTo(replyingTo === ticket.id ? null : ticket.id)} style={{ padding: '8px 16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>
                        {replyingTo === ticket.id ? 'Cancel Reply' : 'Reply & Resolve'}
                      </button>
                      <button onClick={() => handleResolveTicket(ticket.id)} style={{ padding: '8px 16px', backgroundColor: 'transparent', color: '#28a745', border: '1px solid #28a745', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>
                        Mark Resolved (No Reply)
                      </button>
                    </div>
                  </div>

                  {/* The User's Message */}
                  <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '6px', border: '1px solid #dee2e6', whiteSpace: 'pre-wrap', color: '#333', marginBottom: replyingTo === ticket.id ? '15px' : '0' }}>
                    {ticket.message}
                  </div>

                  {/* The Reply Box */}
                  {replyingTo === ticket.id && (
                    <form onSubmit={(e) => handleSendReply(e, ticket)} style={{ backgroundColor: '#e6f2ff', padding: '15px', borderRadius: '6px', border: '1px solid #b8daff' }}>
                      <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#004085', fontWeight: 'bold' }}>Reply to {ticket.userEmail}</p>
                      <textarea 
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder="Type your response here. This will be emailed directly to the user."
                        rows="4"
                        style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', marginBottom: '10px', boxSizing: 'border-box', resize: 'vertical' }}
                        autoFocus
                      />
                      <button type="submit" style={{ padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>
                        Send Email & Resolve Ticket
                      </button>
                    </form>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'broadcast' && (
        <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', maxWidth: '600px' }}>
          <h2 style={{ margin: '0 0 15px 0' }}>System-Wide Broadcast</h2>
          <p style={{ margin: '0 0 20px 0', color: '#6c757d' }}>Push an alert banner to the top of the homepage for all users.</p>
          
          <form onSubmit={handleSendBroadcast}>
            <textarea 
              value={broadcastMessage}
              onChange={(e) => setBroadcastMessage(e.target.value)}
              placeholder="e.g., We are performing scheduled maintenance tonight at 2AM EST."
              rows="3"
              style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box', marginBottom: '15px' }}
            />
            
            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input type="radio" value="info" checked={broadcastType === 'info'} onChange={(e) => setBroadcastType(e.target.value)} />
                ℹ️ Info (Blue)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input type="radio" value="warning" checked={broadcastType === 'warning'} onChange={(e) => setBroadcastType(e.target.value)} />
                ⚠️ Warning (Yellow)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input type="radio" value="success" checked={broadcastType === 'success'} onChange={(e) => setBroadcastType(e.target.value)} />
                ✅ Success (Green)
              </label>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" style={{ padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Push Alert Live</button>
              <button type="button" onClick={handleClearBroadcast} style={{ padding: '10px 20px', backgroundColor: '#f8f9fa', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Clear Active Alert</button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'categories' && (
        <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', maxWidth: '600px' }}>
          <h2 style={{ margin: '0 0 15px 0' }}>Manage Categories</h2>
          <p style={{ margin: '0 0 20px 0', color: '#6c757d' }}>Add or remove categories from the main search filters.</p>
          
          <form onSubmit={handleAddCategory} style={{ display: 'flex', gap: '10px', marginBottom: '30px' }}>
            <input 
              type="text" 
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="e.g., Smartwatches"
              style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
            />
            <button type="submit" style={{ padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Add</button>
          </form>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {categories.length === 0 ? <p style={{ color: '#888', fontStyle: 'italic' }}>No dynamic categories found.</p> : categories.map(cat => (
              <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 15px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '6px' }}>
                <span style={{ fontWeight: 'bold', color: '#495057' }}>{cat}</span>
                <button onClick={() => handleDeleteCategory(cat)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

// Internal Styles
const statCardStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', borderLeft: '5px solid #28a745' };
const tabStyle = (isActive) => ({ padding: '10px 20px', backgroundColor: isActive ? '#1a1a1a' : 'transparent', color: isActive ? 'white' : '#495057', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' });
const btnStyle = (bg, color = 'white') => ({ padding: '6px 12px', backgroundColor: bg, color: color, border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' });